package com.evoapps.evoshift.data.dto.auth

import com.evoapps.evoshift.domain.billing.BillingCustomer
import com.evoapps.evoshift.domain.billing.BillingStore
import com.evoapps.evoshift.domain.billing.Entitlement
import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * `owners/{uid}/billing/revenuecat` (SH-38): RevenueCat's `customer_info` as the webhook
 * (functions/revenuecat.js) writes it. Only the fields the app reads; RevenueCat's names.
 */
@Serializable
data class BillingMirrorDto(
    val entitlements: Map<String, MirrorEntitlementDto> = emptyMap(),
    val subscriptions: Map<String, MirrorSubscriptionDto> = emptyMap(),
    @SerialName("management_url") val managementUrl: String? = null
)

@Serializable
data class MirrorEntitlementDto(
    @SerialName("expires_date") val expiresDate: String? = null,
    @SerialName("product_identifier") val productIdentifier: String? = null,
    /** Google Play's base plan (`monthly`), which the app's product ids carry as `subscription:basePlan`. */
    @SerialName("product_plan_identifier") val productPlanIdentifier: String? = null,
    @SerialName("grace_period_expires_date") val gracePeriodExpiresDate: String? = null
)

@Serializable
data class MirrorSubscriptionDto(
    val store: String? = null,
    @SerialName("unsubscribe_detected_at") val unsubscribeDetectedAt: String? = null,
    @SerialName("billing_issues_detected_at") val billingIssuesDetectedAt: String? = null,
    @SerialName("is_sandbox") val isSandbox: Boolean = false,
    @SerialName("expires_date") val expiresDate: String? = null,
    @SerialName("grace_period_expires_date") val gracePeriodExpiresDate: String? = null
)

/** The mirror as the domain's [BillingCustomer]; a grace period extends the expiry. */
fun BillingMirrorDto.toBillingCustomer(uid: String): BillingCustomer = BillingCustomer(
    uid = uid,
    entitlements = entitlements.map { (id, entitlement) ->
        val subscription = entitlement.productIdentifier?.let { subscriptions[it] ?: subscriptions["$it:${entitlement.productPlanIdentifier}"] }
        Entitlement(
            id = id,
            productId = entitlement.productIdentifier?.let { product ->
                entitlement.productPlanIdentifier?.takeIf { it.isNotBlank() && ':' !in product }?.let { "$product:$it" } ?: product
            },
            expiresAt = endOf(entitlement.expiresDate, entitlement.gracePeriodExpiresDate),
            willRenew = subscription?.unsubscribeDetectedAt == null,
            store = BillingStore.fromRevenueCat(subscription?.store),
            isSandbox = subscription?.isSandbox ?: false,
            hasBillingIssue = subscription?.billingIssuesDetectedAt != null
        )
    },
    managementUrl = managementUrl,
    subscriptionEnds = subscriptions.mapValues { (_, subscription) ->
        endOf(subscription.expiresDate, subscription.gracePeriodExpiresDate)
    }
)

/** The later of an expiry and its grace period; null: no end. */
private fun endOf(expiresDate: String?, gracePeriodExpiresDate: String?): Instant? {
    val expires = parseInstant(expiresDate)
    val grace = parseInstant(gracePeriodExpiresDate)
    return if (expires != null && grace != null && grace > expires) grace else expires
}

/** A date RevenueCat sent; unreadable ones count as "far future" so a typo never locks anyone out. */
private fun parseInstant(value: String?): Instant? =
    value?.let { runCatching { Instant.parse(it) }.getOrElse { Instant.DISTANT_FUTURE } }
